import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  Suspense,
} from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import HeadingBanner from "../components/HeadingBanner";
import {
  whoami,
  getClasses,
  getTeachers,
  getSchools,
  getCountryGrades,
  getTrialistsBySchool,
  getScreeningWindowStatuses,
  getPilotBySchoolId,
  getCountryId,
  getPurchasesBySchool
} from "../services/APIClient";
import "../components/ClassCard.css";

// Lazy Loaded Components
const ResultsDashCard = React.lazy(() =>
  import("../components/ResultsDashCard")
);
const LeftPanel = React.lazy(() => import("../components/LeftPanel"));
const AlpacaInfoGesture = React.lazy(() =>
  import("../components/AlpacaInfoGesture")
);
const ClassFilter = React.lazy(() => import("../components/ClassFilter"));
const FreeTrialBanner = React.lazy(() =>
  import("../components/FreeTrialBanner")
);
const OrientationPrompt = React.lazy(() =>
  import("../components/OrientationPrompt")
);

function ResultsDashboard() {
  const navigate = useNavigate();
  const [schoolid, setSchoolid] = useState(null);
  const [teacherid, setTeacherid] = useState(null);
  const [name, setName] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [firstname, setFirstname] = useState(null);
  const [speechText, setSpeechText] = useState(null);
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [rollNumber, setRollNumber] = useState(null);
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
  const [teacherRoles, setTeacherRoles] = useState(null);
  const [trialistDetails, setTrialistDetails] = useState(null);
  const [freeDaysLeft, setFreeDaysLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  
  const [countryid, setCountryid] = useState(null);
  const [existingConversionMethods, setExistingConversionMethods] = useState([]);
  const [pilotData, setPilotData] = useState(null);
  const [screeningWindowData, setScreeningWindowData] = useState(null);
  const [latestPurchase, setLatestPurchase] = useState(null);
  const DEFAULT_COUNTRY_ID = '07d45b79-24b1-435f-b82a-93861b23a86e';
  const isPilot = pilotData && pilotData.length > 0 && pilotData[0].active;


  const now = new Date();

  const { expired = false, purchased = false, purchased_2425 = false, purchased_2526 = false } = trialistDetails || {};

  // Memoized derived state
  const sortedFilteredClasses = useMemo(() => {
    return [...filteredClasses].sort(
      (a, b) => new Date(b.date_created) - new Date(a.date_created)
    );
  }, [filteredClasses]);

  const handleOrientationChange = useCallback(() => {
    setIsPortrait(window.innerWidth < window.innerHeight);
  }, []);

  const handleSENCOView = useCallback(() => {
    navigate("/sencoresults/detail", {
      state: { teachers, name, gradeLevelOptions, teacherid, schoolid },
    });
  }, [navigate, teachers, name, gradeLevelOptions, teacherid, schoolid]);

  useEffect(() => {
    window.addEventListener("resize", handleOrientationChange);
    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, [handleOrientationChange]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const response = await whoami();
        const { schoolid, id, name, additionalRoles } =
          response.data.stakeholder;
        setSchoolid(schoolid);
        setTeacherid(id);
        setName(name);
        setTeacherRoles(additionalRoles);
        setFirstname(name.split(" ")[0]);
      } catch (error) {
        console.error("Error fetching user information:", error);
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        const [teachersResponse, classesResponse, schoolsResponse] =
          await Promise.all([
            getTeachers(schoolid),
            getClasses(),
            getSchools(),
          ]);

        setTeachers(teachersResponse);

        const uniqueClasses = Array.from(
          new Set(classesResponse.map((cls) => cls.id))
        ).map((id) => classesResponse.find((cls) => cls.id === id));
        setClasses(uniqueClasses);

        const matchingSchool = schoolsResponse.data.find(
          (school) => school.id === schoolid
        );
        if (matchingSchool) {
          const { rollnumber, countryid } = matchingSchool;
          setRollNumber(rollnumber);
          const gradesResponse = await getCountryGrades(countryid);
          setGradeLevelOptions([gradesResponse]);
        }
      } catch (error) {
        console.error("Error fetching school data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (schoolid) fetchSchoolData();
  }, [schoolid]);

  useEffect(() => {
    const fetchTrialistDetails = async () => {
      try {
        const response = await getTrialistsBySchool(schoolid);
        if (response.length > 0) {
          const details = response[0];
          setTrialistDetails(details);

          const currentDate = new Date();
          const endDate = new Date(details.end_date);
          setFreeDaysLeft(
            Math.max(
              0,
              Math.ceil((endDate - currentDate) / (1000 * 60 * 60 * 24))
            )
          );
        }
      } catch (error) {
        console.error("Error fetching trialist details:", error);
      }
    };

    if (schoolid) fetchTrialistDetails();
  }, [schoolid]);

  useEffect(() => {
    const fetchLatestPurchase = async () => {
      if (!schoolid) return;

      try {
        const purchaseResponse = await getPurchasesBySchool(schoolid, {
          order: "next_payment_date DESC",
          limit: 1,
        });

        setLatestPurchase(
          Array.isArray(purchaseResponse) && purchaseResponse.length > 0
            ? purchaseResponse[0]
            : null
        );
      } catch (error) {
        console.error("Error fetching purchase data:", error);
        setLatestPurchase(null);
      }
    };

    fetchLatestPurchase();
  }, [schoolid]);

  useEffect(() => {
    setSpeechText(
      `Whoa, slow down there ${firstname}. We need to create a class first. Click into "Classrooms" on the left to get this started.`
    );
  }, [firstname]);


  useEffect(() => {
    if (schoolid) {
      console.log('🔍 Fetching countryid for schoolid:', schoolid);
      getCountryId(schoolid)
        .then((response) => {
          if (response.countryId) {
            console.log('✅ Country ID response:', response);
            setCountryid(response.countryId);
          } else {
            console.warn("⚠️ No country ID found for school:", response.message);
            setCountryid(null);
          }
        })
        .catch((error) => {
          console.error("❌ Failed to fetch country ID:", error);
          setCountryid(null);
        });
    }
  }, [schoolid]);

  useEffect(() => {
    console.log('🚀 fetchPilotAndScreeningData useEffect triggered');
    console.log('📍 Current values:', { schoolid, countryid });

    const fetchPilotAndScreeningData = async () => {
      if (!schoolid || !countryid) {
        console.log('❌ Missing required values, returning early:', { schoolid, countryid });
        return;
      }

      try {
        // Fetch pilot data
        console.log('🔍 Fetching pilot data for schoolid:', schoolid);
        const pilotResponse = await getPilotBySchoolId(schoolid);
        console.log('📊 Pilot data:', pilotResponse);
        setPilotData(pilotResponse);

        // Fetch screening window status
        console.log('🔍 Fetching screening window for countryid:', countryid);

        let screeningResponse = await getScreeningWindowStatuses({
          countryid: countryid,
          active: true
        });

        console.log('📊 Screening window data (original):', screeningResponse);

        // If no results, fallback to default
        if (!screeningResponse || screeningResponse.length === 0) {
          console.log('⚠️ Using default countryid:', DEFAULT_COUNTRY_ID);
          screeningResponse = await getScreeningWindowStatuses({
            countryid: DEFAULT_COUNTRY_ID,
            active: true
          });
          console.log('📊 Screening window data (default):', screeningResponse);
        }

        setScreeningWindowData(screeningResponse);

      } catch (error) {
        console.error('❌ Error fetching pilot/screening data:', error);
      }
    };

    fetchPilotAndScreeningData();
  }, [schoolid, countryid]);

  return (
    <div>
      <Container fluid className="vh-100">
        <HeadingBanner
          name={`${name}'s Results`}
          isLoggedIn={true}
          cloud={true}
          schoolid={schoolid}
          teacherid={teacherid}
          pilotData={pilotData}
          screeningWindowData={screeningWindowData}
        />
        <Row className="h-100">
          <Suspense fallback={<div>Loading Left Panel...</div>}>
            <LeftPanel />
          </Suspense>
          <Col sm={{ span: 10, offset: 2 }} className="blue-background">
            {loading ? (
              <div className="spinner-container">
                <div className="big-ball-spinner"></div>
              </div>
            ) : (
              <Row className="row-position">
                <Suspense fallback={<div>Loading Free Trial Banner...</div>}>
                  <FreeTrialBanner
                    schoolid={schoolid}
                    teacherName={name}
                    freeDaysLeft={freeDaysLeft}
                    trialistDetails={trialistDetails}
                    pilotData={pilotData}
                  />
                </Suspense>
                {classes.length > 0 && (
                  <Suspense fallback={<div>Loading Class Filter...</div>}>
                    <ClassFilter
                      classes={classes}
                      setFilteredClasses={setFilteredClasses}
                      gradeLevelOptions={gradeLevelOptions}
                      teacherRoles={teacherRoles}
                      schoolid={schoolid}
                    />
                  </Suspense>
                )}
                {teacherRoles?.some((role) =>
                  ["SENCO", "SET", "Special Educator/SET"].includes(role)
                ) && (
                    <Row>
                      <Col className="text-start">
                        <button className="blue-button" onClick={handleSENCOView}>
                          SENCO Results View
                        </button>
                      </Col>
                    </Row>
                  )}
                {classes.length === 0 ? (
                  <Col className="alpaca-message-positioner">
                    <Suspense
                      fallback={<div>Loading Alpaca Info Gesture...</div>}
                    >
                      <AlpacaInfoGesture speechText={speechText} />
                    </Suspense>
                  </Col>
                ) : (
                  sortedFilteredClasses.map((item, index) => (
                    <Suspense
                      key={item.id}
                      fallback={<div>Loading Results Dash Card...</div>}
                    >
                      <ResultsDashCard
                        key={`${item.id}-${index}`}
                        teachers={teachers}
                        classDetails={item}
                        rollNumber={rollNumber}
                        teacherid={teacherid}
                        schoolid={schoolid}
                      />
                    </Suspense>
                  ))
                )}
              </Row>
            )}
          </Col>
        </Row>
      </Container>
      {isPortrait && (
        <Suspense fallback={<div>Loading Orientation Prompt...</div>}>
          <OrientationPrompt />
        </Suspense>
      )}
    </div>
  );
}

export default ResultsDashboard;

// import React, { useEffect, useState } from "react";
// import { Container, Row, Col, Button } from "react-bootstrap"; // Import Button from react-bootstrap
// import { Link, useNavigate } from "react-router-dom"; // Import useNavigate
// import HeadingBanner from "../components/HeadingBanner";
// import ResultsDashCard from "../components/ResultsDashCard";
// import LeftPanel from "../components/LeftPanel";
// import AlpacaInfoGesture from "../components/AlpacaInfoGesture";
// import {
//   whoami,
//   getClasses,
//   getTeachers,
//   getSchools,
//   getCountryGrades,
//   getTrialistsBySchool
// } from "../services/APIClient";
// import ClassFilter from "../components/ClassFilter";
// import OrientationPrompt from "../components/OrientationPrompt";
// import FreeTrialBanner from "../components/FreeTrialBanner";

// function ResultsDashboard() {
//   const navigate = useNavigate(); // Initialize navigate
//   const [schoolid, setSchoolid] = useState(null);
//   const [teacherid, setTeacherid] = useState(null);
//   const [name, setName] = useState(null);
//   const [teachers, setTeachers] = useState(null);
//   const [firstname, setFirstname] = useState(null);
//   const [speechText, setSpeechText] = useState(null);
//   const [classes, setClasses] = useState([]);
//   const [rollNumber, setRollNumber] = useState(null);
//   const [filteredClasses, setFilteredClasses] = useState([]);
//   const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
//   const [teacherRoles, setTeacherRoles] = useState(null);
//   const [isPortrait, setIsPortrait] = useState(
//     window.matchMedia("(orientation: portrait)").matches
//   );

//   const [trialistDetails, setTrialistDetails] = useState(null);
//   const [freeDaysLeft, setFreeDaysLeft] = useState(null);
//   const [showTrialEndedModal, setShowTrialEndedModal] = useState(false);
//   const {
//     expired = false,
//     purchased = false,
//   } = trialistDetails || {};

//   console.log(schoolid, "SIDDD");

//   useEffect(() => {
//     const handleOrientationChange = () => {
//       setIsPortrait(window.innerWidth < window.innerHeight);
//     };

//     window.addEventListener("resize", handleOrientationChange);

//     return () => {
//       window.removeEventListener("resize", handleOrientationChange);
//     };
//   }, []);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     whoami().then((response) => {
//       setSchoolid(response.data.stakeholder.schoolid);
//       setTeacherid(response.data.stakeholder.id);
//       setName(response.data.stakeholder.name);
//       setTeacherRoles(response.data.stakeholder.additionalRoles);
//     });
//   }, []);

//   useEffect(() => {
//     async function fetchSchoolData() {
//       try {
//         const teachersResponse = await getTeachers(schoolid);
//         setTeachers(teachersResponse);

//         let classesResponse = await getClasses();

//         classesResponse = classesResponse.filter(
//           (classItem, index, self) =>
//             index === self.findIndex((t) => t.id === classItem.id)
//         );

//         classesResponse.sort(
//           (a, b) => new Date(b.date_created) - new Date(a.date_created)
//         );
//         setClasses(classesResponse);

//         const schoolsResponse = await getSchools();
//         const matchingSchool = schoolsResponse.data.find(
//           (school) => school.id === schoolid
//         );

//         if (matchingSchool) {
//           const rollNumber = matchingSchool.rollnumber;
//           const countryid = matchingSchool.countryid;
//           setRollNumber(rollNumber);

//           getCountryGrades(countryid)
//             .then((gradesResponse) => {
//               setGradeLevelOptions([gradesResponse]);
//             })
//             .catch((error) => {
//               console.error("Error fetching country grades:", error);
//             });
//         }
//       } catch (error) {
//         console.error("Error fetching school data:", error);
//       }
//     }

//     if (schoolid) {
//       fetchSchoolData().finally(() => {
//         setLoading(false);
//       });
//     }
//   }, [schoolid]);

//   useEffect(() => {
//     if (name) {
//       let nameArray = name.split(" ");
//       setFirstname(nameArray[0]);
//     }
//   }, [name, schoolid]);

//   useEffect(() => {
//     setSpeechText(
//       `Whoa, slow down there ${firstname}. We need to create a class first. Click into "Admin" on the Left to get this started`
//     );
//   }, [firstname]);

//   useEffect(() => {
//     window.HubSpotConversations?.widget?.refresh();
//   }, []);

//   useEffect(() => {
//     const fetchTrialistDetails = async () => {
//       try {
//         const response = await getTrialistsBySchool(schoolid); // Fetch data
//         if (response.length > 0) {
//           const details = response[0]; // Assuming we use the first trial entry
//           setTrialistDetails(details);

//           // Calculate freeDaysLeft
//           const currentDate = new Date();
//           const endDate = new Date(details.end_date);
//           const timeDiff = endDate - currentDate; // Difference in milliseconds
//           const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)); // Convert to days
//           setFreeDaysLeft(daysLeft > 0 ? daysLeft : 0); // Ensure non-negative value
//         }
//       } catch (error) {
//         console.error("Failed to fetch trialist details:", error);
//       }
//     };
//     fetchTrialistDetails();
//   }, [schoolid]);

//   // Function to handle button click
//   const handleSENCOView = () => {
//     // Navigate to the same route as the ResultsDashCard link
//     navigate("/sencoresults/detail", {
//       state: {
//         // classDetails: { classes: filteredClasses }, // Modify as needed
//         teachers,
//         name,
//         gradeLevelOptions,
//         teacherid,
//         schoolid,
//       },
//     });
//   };

//   console.log(teacherRoles);

//   return (
//     <div>
//       <Container fluid className="vh-100">
//         <HeadingBanner
//           name={`${name}'s Results`}
//           isLoggedIn={true}
//           cloud={true}
//           teacherid={teacherid}
//           purchased={purchased}
//         />

//         <Row className="h-100">
//           <LeftPanel />
//           <Col sm={{ span: 10, offset: 2 }} className="blue-background">
//             {loading ? (
//               <div className="spinner-container">
//                 <div className="big-ball-spinner"></div>
//               </div>
//             ) : (
//               <Row className="row-position">
//                 {!purchased ? (
//                   <FreeTrialBanner
//                     schoolid={schoolid}
//                     teacherName={name}
//                     freeDaysLeft={freeDaysLeft}
//                     trialistDetails={trialistDetails}
//                   />
//                 ) : (
//                   null
//                 )}
//                 {classes.length > 0 && (
//                   <>
//                     <ClassFilter
//                       classes={classes}
//                       setFilteredClasses={setFilteredClasses}
//                       gradeLevelOptions={gradeLevelOptions}
//                       teacherRoles={teacherRoles}
//                     />
//                     <br />
//                     {/* Conditionally show the SENCO button here */}
//                     {teacherRoles?.includes("SENCO") && (
//                       <Row>
//                         <Col className="text-start">
//                           <button
//                             className="blue-button"
//                             onClick={handleSENCOView}
//                           >
//                             SENCO Results View
//                           </button>
//                         </Col>
//                       </Row>
//                     )}
//                   </>
//                 )}
//                 {classes.length === 0 ? (
//                   <>
//                     <Col className="col-2"></Col>
//                     <Col className="alpaca-message-positioner">
//                       <AlpacaInfoGesture speechText={speechText} />
//                     </Col>
//                   </>
//                 ) : (
//                   filteredClasses.map((item, index) => (
//                     <ResultsDashCard
//                       key={`${item.id}-${index}`}
//                       teachers={teachers}
//                       classDetails={item}
//                       rollNumber={rollNumber}
//                       teacherid={teacherid}
//                       schoolid={schoolid}
//                     />
//                   ))
//                 )}
//               </Row>
//             )}
//           </Col>
//         </Row>
//       </Container>
//       {isPortrait && <OrientationPrompt />}
//     </div>
//   );
// }

// export default ResultsDashboard;
